import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import {
  ArrowLeft, Save, User, Building2, Phone, Mail, MapPin, Plus, X,
  Check, AlertCircle, Pencil, Trash2, CheckCircle, Clock
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Gastro categories - matching customer portal
const GASTRO_CATEGORIES = [
  { id: 'pos_payment', name: 'Kassensystem & Kartenzahlung' },
  { id: 'reservation', name: 'Reservierungssysteme' },
  { id: 'staff_planning', name: 'Personal- und Schichtplanung' },
  { id: 'smart_order', name: 'Smarte Order-Lösungen' },
  { id: 'delivery', name: 'Delivery-Integration' },
  { id: 'loyalty', name: 'Loyalty & Kundenbindung' },
  { id: 'optimization', name: 'Digitale Betriebsoptimierung' },
  { id: 'social_media', name: 'Social Media' },
  { id: 'website_reviews', name: 'Website- & Bewertungsmanagement' },
];

// Service types
const SERVICE_TYPES = [
  { id: 'support', name: 'Support' },
  { id: 'business_analysis', name: 'Business Analyse' },
  { id: 'social_media', name: 'Social Media' },
  { id: 'website', name: 'Website Management' },
];

const STATUS_OPTIONS = [
  { value: 'not_active', label: 'Nicht aktiv', color: 'bg-off text-ink/80' },
  { value: 'in_consultation', label: 'In Beratung', color: 'bg-amber-100 text-amber-700' },
  { value: 'active', label: 'Aktiv', color: 'bg-green-100 text-green-700' },
];

const AdminCustomerDetail = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [locations, setLocations] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [gastroSystems, setGastroSystems] = useState([]);
  const [services, setServices] = useState([]);
  const [activeTab, setActiveTab] = useState('contact');
  
  // Edit states for each tab
  const [editMode, setEditMode] = useState({
    contact: false,
    gastro: false,
    services: false
  });
  
  // Edited data (local copies for editing)
  const [editedCustomer, setEditedCustomer] = useState(null);
  const [editedGastroSystems, setEditedGastroSystems] = useState([]);
  const [editedServices, setEditedServices] = useState([]);
  
  // Location modal
  const [locationModal, setLocationModal] = useState({ open: false, data: null, isNew: false });

  useEffect(() => {
    fetchCustomerData();
  }, [userId]);

  // Fetch location-specific data when selected location changes
  useEffect(() => {
    if (selectedLocationId) {
      fetchLocationData(selectedLocationId);
    }
  }, [selectedLocationId]);

  const fetchCustomerData = async () => {
    setLoading(true);
    try {
      const [userRes, locRes] = await Promise.all([
        axios.get(`${API}/admin/users/${userId}`),
        axios.get(`${API}/admin/users/${userId}/locations`),
      ]);
      setCustomer(userRes.data);
      setEditedCustomer(userRes.data);
      setLocations(locRes.data);
      
      // Select first location by default
      if (locRes.data.length > 0) {
        setSelectedLocationId(locRes.data[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch customer data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLocationData = async (locationId) => {
    try {
      const [systemsRes, servicesRes] = await Promise.all([
        axios.get(`${API}/admin/locations/${locationId}/gastro-systems`),
        axios.get(`${API}/admin/locations/${locationId}/services`),
      ]);
      setGastroSystems(systemsRes.data);
      setEditedGastroSystems(JSON.parse(JSON.stringify(systemsRes.data)));
      setServices(servicesRes.data);
      setEditedServices(JSON.parse(JSON.stringify(servicesRes.data)));
    } catch (error) {
      console.error('Failed to fetch location data:', error);
    }
  };

  // Toggle edit mode for a specific tab
  const toggleEditMode = (tab) => {
    if (editMode[tab]) {
      // Cancel - revert to original data
      if (tab === 'contact') setEditedCustomer(customer);
      if (tab === 'gastro') setEditedGastroSystems(JSON.parse(JSON.stringify(gastroSystems)));
      if (tab === 'services') setEditedServices(JSON.parse(JSON.stringify(services)));
    }
    setEditMode(prev => ({ ...prev, [tab]: !prev[tab] }));
  };

  // Save functions
  const handleSaveContact = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/users/${userId}`, editedCustomer);
      setCustomer(editedCustomer);
      setEditMode(prev => ({ ...prev, contact: false }));
    } catch (error) {
      console.error('Failed to save customer:', error);
      const errorMsg = error.response?.data?.detail || error.response?.data?.message || error.message || 'Fehler beim Speichern';
      alert(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveGastro = async () => {
    setSaving(true);
    try {
      for (const system of editedGastroSystems) {
        await axios.put(`${API}/admin/locations/${selectedLocationId}/gastro-systems/${system.category}`, {
          status: system.status,
          provider: system.provider || system.current_system_name || '',
          notes: system.notes || ''
        });
      }
      setGastroSystems(JSON.parse(JSON.stringify(editedGastroSystems)));
      setEditMode(prev => ({ ...prev, gastro: false }));
    } catch (error) {
      console.error('Failed to save gastro systems:', error);
      const errorMsg = error.response?.data?.detail || error.response?.data?.message || error.message || 'Fehler beim Speichern';
      alert(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveServices = async () => {
    setSaving(true);
    try {
      for (const service of editedServices) {
        const originalService = services.find(s => s.service_type === service.service_type);
        if (!originalService || originalService.status !== service.status) {
          await axios.put(`${API}/admin/locations/${selectedLocationId}/services/${service.service_type}`, {
            status: service.status
          });
        }
      }
      setServices(JSON.parse(JSON.stringify(editedServices)));
      setEditMode(prev => ({ ...prev, services: false }));
    } catch (error) {
      console.error('Failed to save services:', error);
      const errorMsg = error.response?.data?.detail || error.response?.data?.message || error.message || 'Fehler beim Speichern';
      alert(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    } finally {
      setSaving(false);
    }
  };

  const handleApproveUser = async () => {
    try {
      await axios.put(`${API}/admin/users/${userId}/approve`);
      setCustomer(prev => ({ ...prev, status: 'active' }));
      setEditedCustomer(prev => ({ ...prev, status: 'active' }));
    } catch (error) {
      console.error('Failed to approve user:', error);
      const errorMsg = error.response?.data?.detail || error.response?.data?.message || error.message || 'Fehler beim Freischalten';
      alert(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    }
  };

  // Update functions for edited data
  const updateEditedGastroSystem = (categoryId, field, value) => {
    setEditedGastroSystems(prev => {
      const existing = prev.find(s => s.category === categoryId);
      if (existing) {
        return prev.map(s => s.category === categoryId ? { ...s, [field]: value } : s);
      } else {
        return [...prev, { category: categoryId, status: 'not_active', provider: '', notes: '', [field]: value }];
      }
    });
  };

  const updateEditedService = (serviceId, status) => {
    setEditedServices(prev => {
      const existing = prev.find(s => s.service_type === serviceId);
      if (existing) {
        return prev.map(s => s.service_type === serviceId ? { ...s, status } : s);
      } else {
        return [...prev, { service_type: serviceId, status }];
      }
    });
  };

  const handleSaveLocation = async () => {
    try {
      if (locationModal.isNew) {
        await axios.post(`${API}/admin/users/${userId}/locations`, locationModal.data);
      } else {
        await axios.put(`${API}/admin/users/${userId}/locations/${locationModal.data.id}`, locationModal.data);
      }
      setLocationModal({ open: false, data: null, isNew: false });
      fetchCustomerData();
    } catch (error) {
      console.error('Failed to save location:', error);
      const errorMsg = error.response?.data?.detail || error.response?.data?.message || error.message || 'Fehler beim Speichern';
      alert(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    }
  };

  const handleDeleteLocation = async (locationId) => {
    if (!window.confirm('Standort wirklich löschen?')) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}/locations/${locationId}`);
      fetchCustomerData();
    } catch (error) {
      console.error('Failed to delete location:', error);
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-lilac/50 rounded-none w-1/3"></div>
          <div className="h-64 bg-lilac/50 rounded-none"></div>
        </div>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="p-8 text-center">
        <p className="text-ink/80">Kunde nicht gefunden</p>
        <Button onClick={() => navigate('/admin')} className="mt-4">
          Zurück zur Übersicht
        </Button>
      </div>
    );
  }

  const selectedLocation = locations.find(l => l.id === selectedLocationId);

  return (
    <div className="p-6 lg:p-8" data-testid="admin-customer-detail">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" onClick={() => navigate('/admin')} className="p-2">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-ink">{customer.company_name}</h1>
          <p className="text-ink/60">{customer.name}</p>
        </div>
        {customer.status === 'pending_approval' && (
          <Button
            onClick={handleApproveUser}
            className="btn--wa"
          >
            <CheckCircle className="w-4 h-4 mr-2" />
            Freischalten
          </Button>
        )}
      </div>

      {/* Status Badge */}
      {customer.status === 'pending_approval' && (
        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-none flex items-center gap-3">
          <Clock className="w-5 h-5 text-amber-600" />
          <div>
            <p className="font-medium text-amber-800">Wartet auf Freischaltung</p>
            <p className="text-sm text-amber-600">Dieser Nutzer kann sich noch nicht einloggen.</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-ink/15">
        {[
          { id: 'contact', label: 'Kontakt & Standorte' },
          { id: 'gastro', label: 'Gastro-Landschaft' },
          { id: 'services', label: 'Services' },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'border-purple text-purple'
                : 'border-transparent text-ink/60 hover:text-ink/80'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Contact & Locations Tab */}
      {activeTab === 'contact' && (
        <div className="space-y-6">
          {/* Customer Info */}
          <div className="bg-white rounded-none border-2 border-ink p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-ink">Kontaktdaten</h2>
              {!editMode.contact ? (
                <Button size="sm" variant="outline" onClick={() => toggleEditMode('contact')}>
                  <Pencil className="w-4 h-4 mr-1" /> Bearbeiten
                </Button>
              ) : (
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => toggleEditMode('contact')}>Abbrechen</Button>
                  <Button size="sm" onClick={handleSaveContact} disabled={saving} className="">
                    <Save className="w-4 h-4 mr-1" /> {saving ? 'Speichern...' : 'Speichern'}
                  </Button>
                </div>
              )}
            </div>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="text-ink/60 flex items-center gap-1"><User className="w-3 h-3" /> Name</Label>
                <Input value={editedCustomer?.name || ''} onChange={(e) => setEditedCustomer(prev => ({ ...prev, name: e.target.value }))} disabled={!editMode.contact} className="mt-1" />
              </div>
              <div>
                <Label className="text-ink/60 flex items-center gap-1"><Building2 className="w-3 h-3" /> Firma</Label>
                <Input value={editedCustomer?.company_name || ''} onChange={(e) => setEditedCustomer(prev => ({ ...prev, company_name: e.target.value }))} disabled={!editMode.contact} className="mt-1" />
              </div>
              <div>
                <Label className="text-ink/60 flex items-center gap-1"><Mail className="w-3 h-3" /> E-Mail</Label>
                <Input value={editedCustomer?.email || ''} onChange={(e) => setEditedCustomer(prev => ({ ...prev, email: e.target.value }))} disabled={!editMode.contact} className="mt-1" />
              </div>
              <div>
                <Label className="text-ink/60 flex items-center gap-1"><Phone className="w-3 h-3" /> Telefon</Label>
                <Input value={editedCustomer?.phone || ''} onChange={(e) => setEditedCustomer(prev => ({ ...prev, phone: e.target.value }))} disabled={!editMode.contact} className="mt-1" />
              </div>
            </div>

            <div className="mt-4 pt-4 border-t border-ink/15 flex items-center gap-4">
              <div className="flex items-center gap-2">
                {customer.email_verified ? (
                  <span className="flex items-center gap-1 text-sm text-green-600"><Check className="w-4 h-4" /> E-Mail verifiziert</span>
                ) : (
                  <span className="flex items-center gap-1 text-sm text-amber-600"><AlertCircle className="w-4 h-4" /> Nicht verifiziert</span>
                )}
              </div>
              <span className="text-sm text-ink/60">Registriert: {new Date(customer.created_at).toLocaleDateString('de-DE')}</span>
            </div>
          </div>

          {/* Locations */}
          <div className="bg-white rounded-none border-2 border-ink p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-ink">Standorte ({locations.length})</h2>
              <Button size="sm" onClick={() => setLocationModal({ open: true, isNew: true, data: { name: '', address: '', contact_person: '', phone: '' } })} className="bg-purple hover:bg-deep text-white">
                <Plus className="w-4 h-4 mr-1" /> Standort hinzufügen
              </Button>
            </div>
            
            <div className="space-y-3">
              {locations.length === 0 ? (
                <p className="text-ink/60 text-center py-4">Keine Standorte vorhanden</p>
              ) : (
                locations.map((location) => (
                  <div key={location.id} className="p-4 bg-off rounded-none flex items-start justify-between">
                    <div>
                      <p className="font-medium text-ink">{location.name}</p>
                      {location.address && <p className="text-sm text-ink/60 flex items-center gap-1 mt-1"><MapPin className="w-3 h-3" /> {location.address}</p>}
                      {location.contact_person && <p className="text-sm text-ink/60 mt-1">Ansprechpartner: {location.contact_person}</p>}
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="ghost" onClick={() => setLocationModal({ open: true, isNew: false, data: location })}><Pencil className="w-4 h-4" /></Button>
                      <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700" onClick={() => handleDeleteLocation(location.id)}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Gastro-Landschaft Tab */}
      {activeTab === 'gastro' && (
        <div className="bg-white rounded-none border-2 border-ink p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-ink">Digitale Gastro-Landschaft</h2>
            {!editMode.gastro ? (
              <Button size="sm" variant="outline" onClick={() => toggleEditMode('gastro')}>
                <Pencil className="w-4 h-4 mr-1" /> Bearbeiten
              </Button>
            ) : (
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => toggleEditMode('gastro')}>Abbrechen</Button>
                <Button size="sm" onClick={handleSaveGastro} disabled={saving} className="">
                  <Save className="w-4 h-4 mr-1" /> {saving ? 'Speichern...' : 'Speichern'}
                </Button>
              </div>
            )}
          </div>

          {/* Location Selector */}
          {locations.length > 1 && (
            <div className="mb-6 p-3 bg-off rounded-none">
              <Label className="text-sm text-ink/80 mb-2 block">Standort auswählen:</Label>
              <select
                value={selectedLocationId || ''}
                onChange={(e) => setSelectedLocationId(e.target.value)}
                className="w-full p-2 border-2 border-ink rounded-none"
              >
                {locations.map(loc => (
                  <option key={loc.id} value={loc.id}>{loc.name}</option>
                ))}
              </select>
            </div>
          )}

          {selectedLocation && (
            <p className="text-sm text-ink/60 mb-6">
              Systeme für: <span className="font-medium">{selectedLocation.name}</span>
            </p>
          )}
          
          <div className="grid md:grid-cols-2 gap-4">
            {GASTRO_CATEGORIES.map((category) => {
              const system = editedGastroSystems.find(s => s.category === category.id) || {
                category: category.id, status: 'not_active', provider: '', current_system_name: '', notes: ''
              };
              const providerValue = system.provider || system.current_system_name || '';
              const statusOption = STATUS_OPTIONS.find(s => s.value === system.status);
              
              return (
                <div key={category.id} className="border-2 border-ink rounded-none p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-medium text-ink">{category.name}</h3>
                    {editMode.gastro ? (
                      <select value={system.status} onChange={(e) => updateEditedGastroSystem(category.id, 'status', e.target.value)} className={`text-sm px-2 py-1 rounded-none border-0 cursor-pointer ${statusOption?.color || 'bg-off'}`}>
                        {STATUS_OPTIONS.map(opt => (<option key={opt.value} value={opt.value}>{opt.label}</option>))}
                      </select>
                    ) : (
                      <span className={`text-sm px-2 py-1 rounded-none ${statusOption?.color || 'bg-off'}`}>{statusOption?.label || 'Nicht aktiv'}</span>
                    )}
                  </div>
                  <div className="space-y-2">
                    <div>
                      <Label className="text-xs text-ink/60">Anbieter/System</Label>
                      <Input value={providerValue} onChange={(e) => updateEditedGastroSystem(category.id, 'provider', e.target.value)} placeholder="z.B. orderbird, resmio..." className="h-9 text-sm" disabled={!editMode.gastro} />
                    </div>
                    <div>
                      <Label className="text-xs text-ink/60">Notizen</Label>
                      <Textarea value={system.notes || ''} onChange={(e) => updateEditedGastroSystem(category.id, 'notes', e.target.value)} placeholder="Interne Notizen..." className="text-sm min-h-[60px]" disabled={!editMode.gastro} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Services Tab */}
      {activeTab === 'services' && (
        <div className="bg-white rounded-none border-2 border-ink p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-ink">Aktivierte Services</h2>
            {!editMode.services ? (
              <Button size="sm" variant="outline" onClick={() => toggleEditMode('services')}>
                <Pencil className="w-4 h-4 mr-1" /> Bearbeiten
              </Button>
            ) : (
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => toggleEditMode('services')}>Abbrechen</Button>
                <Button size="sm" onClick={handleSaveServices} disabled={saving} className="">
                  <Save className="w-4 h-4 mr-1" /> {saving ? 'Speichern...' : 'Speichern'}
                </Button>
              </div>
            )}
          </div>

          {/* Location Selector */}
          {locations.length > 1 && (
            <div className="mb-6 p-3 bg-off rounded-none">
              <Label className="text-sm text-ink/80 mb-2 block">Standort auswählen:</Label>
              <select
                value={selectedLocationId || ''}
                onChange={(e) => setSelectedLocationId(e.target.value)}
                className="w-full p-2 border-2 border-ink rounded-none"
              >
                {locations.map(loc => (
                  <option key={loc.id} value={loc.id}>{loc.name}</option>
                ))}
              </select>
            </div>
          )}

          {selectedLocation && (
            <p className="text-sm text-ink/60 mb-6">
              Services für: <span className="font-medium">{selectedLocation.name}</span>
            </p>
          )}
          
          <div className="space-y-4">
            {SERVICE_TYPES.map((service) => {
              const activeService = editedServices.find(s => s.service_type === service.id);
              const status = activeService?.status || 'inactive';
              const isActive = status === 'active';
              const isRequested = status === 'requested';
              const originalService = services.find(s => s.service_type === service.id);
              
              return (
                <div key={service.id} className="flex items-center justify-between p-4 bg-off rounded-none">
                  <div>
                    <p className="font-medium text-ink">{service.name}</p>
                    {isActive && originalService?.activated_at && (
                      <p className="text-sm text-green-600">Aktiviert am: {new Date(originalService.activated_at).toLocaleDateString('de-DE')}</p>
                    )}
                    {isRequested && (
                      <p className="text-sm text-amber-600">Anfrage gestellt</p>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    {isActive ? (
                      <span className="px-3 py-1 bg-green-100 text-green-700 text-sm rounded-none font-medium">Aktiv</span>
                    ) : editMode.services ? (
                      <select
                        value={status}
                        onChange={(e) => updateEditedService(service.id, e.target.value)}
                        className={`text-sm px-3 py-1 rounded-none border-0 ${
                          status === 'requested' ? 'bg-amber-100 text-amber-700' : 
                          status === 'active' ? 'bg-green-100 text-green-700' : 'bg-off text-ink/80'
                        }`}
                      >
                        <option value="inactive">Inaktiv</option>
                        <option value="requested">Angefragt</option>
                        <option value="active">Aktiv</option>
                      </select>
                    ) : (
                      <span className={`px-3 py-1 text-sm rounded-none font-medium ${
                        isRequested ? 'bg-amber-100 text-amber-700' : 'bg-off text-ink/80'
                      }`}>
                        {isRequested ? 'Angefragt' : 'Inaktiv'}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Location Modal */}
      {locationModal.open && (
        <div className="fixed inset-0 bg-ink/70 flex items-center justify-center z-50">
          <div className="bg-white rounded-none border-2 border-ink shadow-ink-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-lg">{locationModal.isNew ? 'Neuer Standort' : 'Standort bearbeiten'}</h3>
              <Button variant="ghost" size="sm" onClick={() => setLocationModal({ open: false, data: null, isNew: false })}><X className="w-5 h-5" /></Button>
            </div>
            <div className="space-y-4">
              <div>
                <Label>Standortname *</Label>
                <Input value={locationModal.data?.name || ''} onChange={(e) => setLocationModal(prev => ({ ...prev, data: { ...prev.data, name: e.target.value } }))} placeholder="z.B. Hauptstandort, Filiale Mitte..." />
              </div>
              <div>
                <Label>Adresse</Label>
                <Input value={locationModal.data?.address || ''} onChange={(e) => setLocationModal(prev => ({ ...prev, data: { ...prev.data, address: e.target.value } }))} placeholder="Straße, PLZ Ort" />
              </div>
              <div>
                <Label>Ansprechpartner</Label>
                <Input value={locationModal.data?.contact_person || ''} onChange={(e) => setLocationModal(prev => ({ ...prev, data: { ...prev.data, contact_person: e.target.value } }))} placeholder="Name des Ansprechpartners" />
              </div>
              <div>
                <Label>Telefon</Label>
                <Input value={locationModal.data?.phone || ''} onChange={(e) => setLocationModal(prev => ({ ...prev, data: { ...prev.data, phone: e.target.value } }))} placeholder="+49..." />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => setLocationModal({ open: false, data: null, isNew: false })}>Abbrechen</Button>
              <Button onClick={handleSaveLocation} className="">{locationModal.isNew ? 'Hinzufügen' : 'Speichern'}</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCustomerDetail;
